import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  KeyRound, Shield, ShieldCheck, ShieldX, Building2, Calendar,
  Save, Loader2, Search, Check, X, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const LicensesTab = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // school_id being edited
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchLicenses(); }, []);

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/licenses`, authHeader());
      setLicenses(res.data.licenses || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const startEdit = (lic) => {
    setEditing(lic.school_id);
    setEditData({
      digital_license: lic.license?.digital_license || false,
      digital_grades: (lic.license?.digital_grades || []).join(', '),
      digital_expires: lic.license?.digital_expires?.split('T')[0] || '',
      toolkit_access: lic.license?.toolkit_access || false,
      toolkit_type: lic.license?.toolkit_type || 'add_on',
      toolkit_expires: lic.license?.toolkit_expires?.split('T')[0] || '',
    });
  };

  const saveLicense = async (schoolId) => {
    setSaving(true);
    try {
      const grades = editData.digital_grades
        ? editData.digital_grades.split(',').map(g => g.trim()).filter(Boolean)
        : [];
      await axios.put(`${API}/licenses/${schoolId}`, {
        digital_license: editData.digital_license,
        digital_grades: grades,
        digital_expires: editData.digital_expires ? `${editData.digital_expires}T23:59:59Z` : null,
        toolkit_access: editData.toolkit_access,
        toolkit_type: editData.toolkit_type,
        toolkit_expires: editData.toolkit_expires ? `${editData.toolkit_expires}T23:59:59Z` : null,
      }, authHeader());
      toast.success('License updated');
      setEditing(null);
      fetchLicenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const revokeLicense = async (schoolId, schoolName) => {
    if (!window.confirm(`Revoke all licenses for ${schoolName}? Teachers will lose access.`)) return;
    try {
      await axios.delete(`${API}/licenses/${schoolId}`, authHeader());
      toast.success('Licenses revoked');
      fetchLicenses();
    } catch { toast.error('Failed to revoke'); }
  };

  const filtered = licenses.filter(l =>
    (l.school_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (l.dbn || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Stats
  const digitalActive = licenses.filter(l => l.license?.digital_license && !isExpired(l.license?.digital_expires)).length;
  const toolkitActive = licenses.filter(l => l.license?.toolkit_access && !isExpired(l.license?.toolkit_expires)).length;
  const noLicense = licenses.filter(l => !l.license?.digital_license && !l.license?.toolkit_access).length;

  return (
    <div className="space-y-5" data-testid="licenses-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>School Licenses</h2>
          <p className="text-xs text-gray-500">Manage Digital License and Teacher Toolkit access per school</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-800">{licenses.length}</div>
          <div className="text-[10px] text-gray-500 uppercase font-bold">Total Schools</div>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{digitalActive}</div>
          <div className="text-[10px] text-gray-500 uppercase font-bold">Digital Active</div>
        </div>
        <div className="bg-white border border-sky-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-sky-600">{toolkitActive}</div>
          <div className="text-[10px] text-gray-500 uppercase font-bold">Toolkit Active</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-400">{noLicense}</div>
          <div className="text-[10px] text-gray-500 uppercase font-bold">No License</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by school name or DBN..." className="pl-9 h-9 border-gray-300" data-testid="license-search" />
      </div>

      {/* License Table */}
      {loading ? (
        <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto text-[#002FA7]" size={24} /></div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-[10px] text-gray-600 uppercase">
                <th className="text-left p-3 font-bold">School</th>
                <th className="text-center p-3 font-bold">Digital License</th>
                <th className="text-center p-3 font-bold">Toolkit Access</th>
                <th className="text-center p-3 font-bold">Expires</th>
                <th className="text-right p-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const lic = item.license || {};
                const isEdit = editing === item.school_id;
                const digitalOk = lic.digital_license && !isExpired(lic.digital_expires);
                const toolkitOk = lic.toolkit_access && !isExpired(lic.toolkit_expires);

                return (
                  <React.Fragment key={item.school_id}>
                    <tr className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-testid={`license-row-${item.school_id}`}>
                      <td className="p-3">
                        <div className="font-medium text-gray-800">{item.school_name}</div>
                        <div className="text-[10px] text-gray-400">{item.dbn} | {item.borough}</div>
                      </td>
                      <td className="p-3 text-center">
                        {digitalOk ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded"><ShieldCheck size={10} /> ACTIVE</span>
                        ) : lic.digital_license && isExpired(lic.digital_expires) ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded"><ShieldX size={10} /> EXPIRED</span>
                        ) : (
                          <span className="text-[10px] text-gray-400">None</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {toolkitOk ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-sky-100 text-sky-700 px-2 py-0.5 rounded">
                            <Wrench size={10} /> {lic.toolkit_type === 'standalone' ? 'STANDALONE' : 'ADD-ON'}
                          </span>
                        ) : lic.toolkit_access && isExpired(lic.toolkit_expires) ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded"><ShieldX size={10} /> EXPIRED</span>
                        ) : (
                          <span className="text-[10px] text-gray-400">None</span>
                        )}
                      </td>
                      <td className="p-3 text-center text-[10px] text-gray-500">
                        {lic.digital_expires || lic.toolkit_expires ? (
                          <div>
                            {lic.digital_expires && <div>Digital: {formatDate(lic.digital_expires)}</div>}
                            {lic.toolkit_expires && <div>Toolkit: {formatDate(lic.toolkit_expires)}</div>}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button onClick={() => startEdit(item)} size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid={`license-edit-${item.school_id}`}>
                            <KeyRound size={12} /> Edit
                          </Button>
                          {(digitalOk || toolkitOk) && (
                            <Button onClick={() => revokeLicense(item.school_id, item.school_name)} size="sm" variant="outline" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 gap-1" data-testid={`license-revoke-${item.school_id}`}>
                              <X size={12} /> Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Edit Row */}
                    {isEdit && (
                      <tr className="bg-blue-50/50 border-t border-blue-200">
                        <td colSpan={5} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Digital License */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-700">Digital License</span>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox" checked={editData.digital_license} onChange={e => setEditData(p => ({ ...p, digital_license: e.target.checked }))} className="rounded" data-testid="edit-digital-toggle" />
                                  <span className="text-xs text-gray-600">{editData.digital_license ? 'Active' : 'Inactive'}</span>
                                </label>
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Grades (comma-separated)</label>
                                <Input value={editData.digital_grades} onChange={e => setEditData(p => ({ ...p, digital_grades: e.target.value }))} placeholder="K, 1, 2, 3-4, 5-6" className="h-8 text-xs border-gray-300" data-testid="edit-digital-grades" />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Expires</label>
                                <Input type="date" value={editData.digital_expires} onChange={e => setEditData(p => ({ ...p, digital_expires: e.target.value }))} className="h-8 text-xs border-gray-300" data-testid="edit-digital-expires" />
                              </div>
                            </div>

                            {/* Toolkit Access */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-700">Teacher Toolkit</span>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox" checked={editData.toolkit_access} onChange={e => setEditData(p => ({ ...p, toolkit_access: e.target.checked }))} className="rounded" data-testid="edit-toolkit-toggle" />
                                  <span className="text-xs text-gray-600">{editData.toolkit_access ? 'Active' : 'Inactive'}</span>
                                </label>
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Type</label>
                                <select value={editData.toolkit_type} onChange={e => setEditData(p => ({ ...p, toolkit_type: e.target.value }))} className="w-full h-8 text-xs border border-gray-300 rounded px-2" data-testid="edit-toolkit-type">
                                  <option value="add_on">Add-On (requires Digital License)</option>
                                  <option value="standalone">Standalone ($499/building/year)</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Expires</label>
                                <Input type="date" value={editData.toolkit_expires} onChange={e => setEditData(p => ({ ...p, toolkit_expires: e.target.value }))} className="h-8 text-xs border-gray-300" data-testid="edit-toolkit-expires" />
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button onClick={() => saveLicense(item.school_id)} disabled={saving} size="sm" className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-1" data-testid="edit-save-btn">
                              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save License
                            </Button>
                            <Button onClick={() => setEditing(null)} size="sm" variant="outline">Cancel</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LicensesTab;
